import {
  isCapitalizedMidSentence,
  letterTokens,
  normalizeWord,
  sentenceIndexAt,
  splitSentences,
  stripPossessive,
  type Token,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Echoes: the same content word used again within a few words — "The study argues… the
 * study's argument argued…". Distinct from a global frequency count (a word used ten
 * times across ten pages is not an echo) and from a doubled word ("the the"), which the
 * Grammar Checker owns.
 *
 * Words are matched by an inflection-only stem: Porter's steps 1 and 5, which strip
 * -s, -es, -ed and -ing and settle the final -e, so argue / argues / argued / arguing
 * meet, but the derivational steps that would merge "general" with "generate" or
 * "university" with "universe" are never run. Irregular forms come from a short table.
 * Function words and the most generic verbs (be, have, make, get…) are never echoes,
 * and a capitalized word mid-sentence is taken to be a name and left alone.
 */

export interface RepetitionOptions {
  /** Flag a repeat this many words or fewer after the previous use. */
  window: number;
  /** Also flag a repeat anywhere in the same sentence, however far apart. */
  sameSentence: boolean;
  /** Words the writer repeats on purpose — key terms. Matched by stem. */
  ignore: readonly string[];
}

export interface EchoPair {
  key: string;
  first: Token;
  second: Token;
  /** Words from the first use to the second: 1 means adjacent. */
  distance: number;
  sameSentence: boolean;
}

export interface EchoToken extends Token {
  key: string;
  /** Words back to the previous use, if that pair is flagged. */
  before: number | null;
  /** Words on to the next use, if that pair is flagged. */
  after: number | null;
}

export interface EchoRow {
  key: string;
  /** The shortest form the text used: "argue" for argue / argued / arguing. */
  label: string;
  forms: string[];
  pairs: number;
  closest: number;
}

export interface RepetitionReport {
  words: number;
  pairs: EchoPair[];
  tokens: EchoToken[];
  /** One row per echoed word, most pairs first. */
  rows: EchoRow[];
}

export const DEFAULT_WINDOW = 30;

const STOPWORDS = new Set([
  "a",
  "about",
  "above",
  "according",
  "across",
  "after",
  "again",
  "against",
  "al",
  "all",
  "almost",
  "along",
  "already",
  "also",
  "although",
  "always",
  "am",
  "among",
  "amongst",
  "an",
  "and",
  "another",
  "any",
  "anyone",
  "anything",
  "are",
  "around",
  "as",
  "at",
  "be",
  "became",
  "because",
  "become",
  "becomes",
  "becoming",
  "been",
  "before",
  "being",
  "below",
  "between",
  "beyond",
  "both",
  "but",
  "by",
  "came",
  "can",
  "cannot",
  "cf",
  "come",
  "comes",
  "coming",
  "could",
  "did",
  "do",
  "does",
  "doing",
  "done",
  "down",
  "during",
  "each",
  "eg",
  "either",
  "else",
  "et",
  "etc",
  "even",
  "ever",
  "every",
  "everyone",
  "everything",
  "few",
  "first",
  "for",
  "from",
  "further",
  "furthermore",
  "gave",
  "get",
  "gets",
  "getting",
  "give",
  "given",
  "gives",
  "giving",
  "go",
  "goes",
  "going",
  "gone",
  "got",
  "gotten",
  "had",
  "has",
  "have",
  "having",
  "he",
  "hence",
  "her",
  "here",
  "hers",
  "herself",
  "him",
  "himself",
  "his",
  "how",
  "however",
  "i",
  "ie",
  "if",
  "in",
  "indeed",
  "instead",
  "into",
  "is",
  "it",
  "its",
  "itself",
  "just",
  "keep",
  "kept",
  "last",
  "least",
  "less",
  "let",
  "like",
  "made",
  "make",
  "makes",
  "making",
  "many",
  "may",
  "me",
  "might",
  "more",
  "moreover",
  "most",
  "much",
  "must",
  "my",
  "myself",
  "near",
  "neither",
  "never",
  "next",
  "no",
  "nor",
  "not",
  "nothing",
  "now",
  "of",
  "off",
  "often",
  "on",
  "once",
  "one",
  "ones",
  "only",
  "onto",
  "or",
  "other",
  "others",
  "otherwise",
  "our",
  "ours",
  "ourselves",
  "out",
  "over",
  "own",
  "per",
  "perhaps",
  "put",
  "quite",
  "rather",
  "really",
  "said",
  "same",
  "saw",
  "say",
  "says",
  "second",
  "see",
  "seem",
  "seemed",
  "seems",
  "seen",
  "several",
  "shall",
  "she",
  "should",
  "since",
  "so",
  "some",
  "someone",
  "something",
  "sometimes",
  "still",
  "such",
  "take",
  "taken",
  "takes",
  "taking",
  "than",
  "that",
  "the",
  "their",
  "theirs",
  "them",
  "themselves",
  "then",
  "there",
  "therefore",
  "these",
  "they",
  "third",
  "this",
  "those",
  "though",
  "through",
  "throughout",
  "thus",
  "to",
  "too",
  "took",
  "toward",
  "towards",
  "two",
  "under",
  "unless",
  "until",
  "up",
  "upon",
  "us",
  "very",
  "via",
  "vs",
  "was",
  "we",
  "well",
  "went",
  "were",
  "what",
  "whatever",
  "when",
  "whenever",
  "where",
  "whereas",
  "whether",
  "which",
  "while",
  "whilst",
  "who",
  "whom",
  "whose",
  "why",
  "will",
  "with",
  "within",
  "without",
  "would",
  "yet",
  "you",
  "your",
  "yours",
  "yourself",
]);

/** Irregular and Latinate forms the stemmer cannot reach, mapped to a shared key. */
const IRREGULAR: Readonly<Record<string, string>> = {
  alias: "alias",
  aliases: "alias",
  analyses: "analysis",
  appendices: "appendix",
  atlas: "atlas",
  arose: "arise",
  arisen: "arise",
  began: "begin",
  begun: "begin",
  bias: "bias",
  biases: "bias",
  bore: "bear",
  borne: "bear",
  brought: "bring",
  built: "build",
  canvas: "canvas",
  chose: "choose",
  chosen: "choose",
  children: "child",
  crises: "crisis",
  criteria: "criterion",
  curricula: "curriculum",
  drawn: "draw",
  drew: "draw",
  fell: "fall",
  fallen: "fall",
  felt: "feel",
  found: "find",
  foci: "focus",
  fungi: "fungus",
  gas: "gas",
  gases: "gas",
  grew: "grow",
  grown: "grow",
  held: "hold",
  hypotheses: "hypothesis",
  indices: "index",
  knew: "know",
  known: "know",
  led: "lead",
  lost: "lose",
  matrices: "matrix",
  meant: "mean",
  men: "man",
  mice: "mouse",
  news: "news",
  nuclei: "nucleus",
  paid: "pay",
  phenomena: "phenomenon",
  ran: "run",
  rose: "rise",
  risen: "rise",
  sent: "send",
  series: "series",
  shown: "show",
  sold: "sell",
  sought: "seek",
  species: "species",
  spoke: "speak",
  spoken: "speak",
  spent: "spend",
  stimuli: "stimulus",
  stood: "stand",
  strata: "stratum",
  taught: "teach",
  theses: "thesis",
  thought: "think",
  told: "tell",
  understood: "understand",
  undertook: "undertake",
  undertaken: "undertake",
  women: "woman",
  won: "win",
  wrote: "write",
  written: "write",
};

/* ---------------------------------------------------------- Porter steps 1 and 5 */

const VOWELS = "aeiou";

function isConsonant(word: string, index: number): boolean {
  const letter = word[index];
  if (VOWELS.includes(letter)) {
    return false;
  }
  if (letter === "y") {
    return index === 0 || !isConsonant(word, index - 1);
  }
  return true;
}

/** Porter's m: the number of vowel–consonant sequences in the stem. */
function measure(stem: string): number {
  let count = 0;
  let previousVowel = false;
  for (let index = 0; index < stem.length; index += 1) {
    const vowel = !isConsonant(stem, index);
    if (previousVowel && !vowel) {
      count += 1;
    }
    previousVowel = vowel;
  }
  return count;
}

function hasVowel(stem: string): boolean {
  for (let index = 0; index < stem.length; index += 1) {
    if (!isConsonant(stem, index)) {
      return true;
    }
  }
  return false;
}

function endsDoubleConsonant(stem: string): boolean {
  const last = stem.length - 1;
  return last > 0 && stem[last] === stem[last - 1] && isConsonant(stem, last);
}

/** Consonant–vowel–consonant at the end, the last not w, x or y: "hop", "rat". */
function endsCvc(stem: string): boolean {
  const last = stem.length - 1;
  if (last < 2) {
    return false;
  }
  return (
    isConsonant(stem, last - 2) &&
    !isConsonant(stem, last - 1) &&
    isConsonant(stem, last) &&
    !"wxy".includes(stem[last])
  );
}

function step1a(word: string): string {
  if (word.endsWith("sses")) {
    return word.slice(0, -2);
  }
  if (word.endsWith("ies")) {
    return word.slice(0, -2);
  }
  // Porter strips any final s; "focus", "basis" and "status" keep theirs here.
  if (word.endsWith("ss") || word.endsWith("us") || word.endsWith("is")) {
    return word;
  }
  return word.endsWith("s") ? word.slice(0, -1) : word;
}

function tidyAfterSuffix(stem: string): string {
  if (stem.endsWith("at") || stem.endsWith("bl") || stem.endsWith("iz")) {
    return `${stem}e`;
  }
  if (endsDoubleConsonant(stem) && !"lsz".includes(stem.at(-1) ?? "")) {
    return stem.slice(0, -1);
  }
  if (measure(stem) === 1 && endsCvc(stem)) {
    return `${stem}e`;
  }
  return stem;
}

function step1b(word: string): string {
  if (word.endsWith("eed")) {
    return measure(word.slice(0, -3)) > 0 ? word.slice(0, -1) : word;
  }
  for (const suffix of ["ed", "ing"]) {
    if (word.endsWith(suffix)) {
      const stem = word.slice(0, -suffix.length);
      return hasVowel(stem) ? tidyAfterSuffix(stem) : word;
    }
  }
  return word;
}

function step1c(word: string): string {
  return word.endsWith("y") && hasVowel(word.slice(0, -1))
    ? `${word.slice(0, -1)}i`
    : word;
}

function step5(word: string): string {
  let stem = word;
  if (stem.endsWith("e")) {
    const base = stem.slice(0, -1);
    const m = measure(base);
    if (m > 1 || (m === 1 && !endsCvc(base))) {
      stem = base;
    }
  }
  if (measure(stem) > 1 && stem.endsWith("ll")) {
    stem = stem.slice(0, -1);
  }
  return stem;
}

/** The key two forms share when they are inflections of one word. */
export function inflectionStem(word: string): string {
  const lower = stripPossessive(normalizeWord(word));
  const irregular = IRREGULAR[lower];
  if (irregular) {
    return irregular;
  }
  return step5(step1c(step1b(step1a(lower))));
}

/* ------------------------------------------------------------------ detection */

const MIN_WORD_LENGTH = 3;

function isContentWord(text: string, token: Token): boolean {
  const lower = stripPossessive(token.lower);
  return (
    lower.length >= MIN_WORD_LENGTH &&
    !STOPWORDS.has(lower) &&
    !lower.includes("'") &&
    !isCapitalizedMidSentence(text, token.start)
  );
}

function ignoredStems(words: readonly string[]): Set<string> {
  return new Set(
    words
      .map((word) => word.trim())
      .filter((word) => word.length > 0)
      .map(inflectionStem)
  );
}

function pairFor(
  previous: { token: Token; position: number; sentence: number },
  current: { token: Token; position: number; sentence: number },
  key: string,
  options: RepetitionOptions
): EchoPair | null {
  const distance = current.position - previous.position;
  const sameSentence =
    previous.sentence >= 0 && previous.sentence === current.sentence;
  // "results results" is a doubled word — the Grammar Checker's job, not an echo.
  if (distance === 1 && previous.token.lower === current.token.lower) {
    return null;
  }
  if (distance <= options.window || (options.sameSentence && sameSentence)) {
    return {
      key,
      first: previous.token,
      second: current.token,
      distance,
      sameSentence,
    };
  }
  return null;
}

function findPairs(
  text: string,
  tokens: Token[],
  options: RepetitionOptions
): EchoPair[] {
  const sentences = splitSentences(text);
  const ignored = ignoredStems(options.ignore);
  const lastSeen = new Map<
    string,
    { token: Token; position: number; sentence: number }
  >();
  const pairs: EchoPair[] = [];
  for (const [position, token] of tokens.entries()) {
    const key = isContentWord(text, token) ? inflectionStem(token.lower) : "";
    if (key === "" || ignored.has(key)) {
      continue;
    }
    const current = {
      token,
      position,
      sentence: sentenceIndexAt(sentences, token.start),
    };
    const previous = lastSeen.get(key);
    const pair = previous ? pairFor(previous, current, key, options) : null;
    if (pair) {
      pairs.push(pair);
    }
    lastSeen.set(key, current);
  }
  return pairs;
}

function echoTokens(pairs: EchoPair[]): EchoToken[] {
  const byStart = new Map<number, EchoToken>();
  const touch = (token: Token, key: string): EchoToken => {
    const existing = byStart.get(token.start);
    if (existing) {
      return existing;
    }
    const created: EchoToken = { ...token, key, before: null, after: null };
    byStart.set(token.start, created);
    return created;
  };
  for (const pair of pairs) {
    touch(pair.first, pair.key).after = pair.distance;
    touch(pair.second, pair.key).before = pair.distance;
  }
  return [...byStart.values()].sort((a, b) => a.start - b.start);
}

function rowsFor(pairs: EchoPair[]): EchoRow[] {
  const rows = new Map<string, EchoRow>();
  for (const pair of pairs) {
    const row = rows.get(pair.key) ?? {
      key: pair.key,
      label: "",
      forms: [],
      pairs: 0,
      closest: Number.POSITIVE_INFINITY,
    };
    row.pairs += 1;
    row.closest = Math.min(row.closest, pair.distance);
    for (const form of [pair.first.lower, pair.second.lower]) {
      if (!row.forms.includes(form)) {
        row.forms.push(form);
      }
    }
    rows.set(pair.key, row);
  }
  for (const row of rows.values()) {
    row.label = row.forms.reduce((shortest, form) =>
      form.length < shortest.length ? form : shortest
    );
  }
  return [...rows.values()].sort(
    (a, b) =>
      b.pairs - a.pairs ||
      a.closest - b.closest ||
      a.label.localeCompare(b.label)
  );
}

export function findRepetitions(
  text: string,
  options: RepetitionOptions
): RepetitionReport {
  const tokens = letterTokens(text);
  const pairs = findPairs(text, tokens, options);
  return {
    words: tokens.length,
    pairs,
    tokens: echoTokens(pairs),
    rows: rowsFor(pairs),
  };
}
